import { api, postNow } from '../api'
import { getMe } from '../me'
import { lineDrawing, type Drawing } from '../picture-tiles'
import { clear, el, PRIVACY_URL } from '../ui'
import { logOut } from './signin'
import { aiRows } from '../ai-consent'
import { downloadCustomers, downloadVisits } from './grid'
import '../account.css'

// Your account (Liam, 4 Nov 2026: "Maybe your account in more", design B https://claude.ai/artifact/RBzLR2GMvWnCRsYZzU6ik1,
// then "Go with recommended"): who you are signed in as, your picture, your lists to download (owners), log out
// everywhere, privacy, and Delete. Apple 5.1.1(v) and Google Play want deleting to start inside the app
// (docs/STORE-APPS.md). An owner's Delete closes the round at once and asks Liam to delete it, who sends their copy
// first and deletes it on his own go (functions/api/me/account.ts); someone on a round takes their own sign-in off it.

const tile = (drawing: Drawing, title: string, words: string, act: (() => void) | null, href?: string): HTMLElement => {
  const inner = [el('span', { class: 'acct-ring', 'aria-hidden': 'true' }, lineDrawing(drawing, 22)), el('b', {}, title), el('span', { class: 'acct-words' }, words)]
  const t = href ? el('a', { class: 'acct-tile', href, target: '_blank', rel: 'noopener' }, ...inner) : el('button', { type: 'button', class: 'acct-tile' }, ...inner)
  if (act) t.addEventListener('click', act)
  return t
}

export function mountAccount(): void {
  const root = document.querySelector<HTMLElement>('#screen')
  if (!root) return
  clear(root)
  const me = getMe()
  const owner = me?.role === 'owner'
  const roundName = me?.business?.name ?? me?.businessName ?? 'your round'
  const page = el('div', { class: 'acct-page' })
  page.append(el('div', { class: 'page-heading' }, el('div', {},
    el('h2', {}, 'Your account'),
    el('p', { class: 'muted' }, [me?.email ?? '', owner ? `Owner of ${roundName}` : `On ${roundName}`].filter(Boolean).join(' · ')))))
  const tiles = el('div', { class: 'acct-tiles' })
  tiles.append(tile('person', 'Your picture', 'Change or remove it', () => document.querySelector<HTMLButtonElement>('#account-photo')?.click()))
  if (owner) {
    const dl = tile('arrowDown', 'Download everything', 'Your customers, visits and payments as spreadsheets', null)
    dl.addEventListener('click', async () => { const b = dl as HTMLButtonElement; await downloadCustomers(b); await downloadVisits(b) })
    tiles.append(dl)
  }
  tiles.append(tile('door', 'Log out everywhere', 'Every phone and computer', () => void logOut(true)))
  tiles.append(tile('lock', 'Privacy', 'How Roundly looks after it', null, PRIVACY_URL))
  page.append(tiles)
  // The AI services: Help answers and voice notes written out, each allowed or off (ai-consent.ts, 4 Nov 2026).
  page.append(aiRows())
  const del = el('button', { type: 'button', class: 'acct-delete' },
    el('span', { class: 'acct-ring acct-ring-red', 'aria-hidden': 'true' }, lineDrawing('bin', 22)),
    el('span', {}, el('b', {}, owner ? 'Delete my round' : 'Delete my account'),
      el('span', { class: 'acct-words' }, owner ? 'Close it now; we delete it after sending you a copy' : `Take your sign-in off ${roundName}`)))
  del.addEventListener('click', () => (owner ? openCloseRound(roundName) : openDeleteMe(roundName)))
  page.append(del)
  root.append(page)
}

let busy = false
function sheet(title: string): { dialog: HTMLDialogElement; body: HTMLElement } {
  const dialog = el('dialog', { class: 'booking-dialog quick-sheet acct-sheet', 'aria-label': title }) as HTMLDialogElement
  const close = el('button', { type: 'button', 'aria-label': 'Close' }, 'Close')
  close.addEventListener('click', () => { if (busy) return; dialog.close(); dialog.remove() })
  // Sending: the sheet stays until the answer is back, so the sign-out after it always happens (Astra).
  dialog.addEventListener('cancel', (e) => { if (busy) e.preventDefault() })
  const body = el('div', { class: 'acct-sheet-body' })
  dialog.append(el('header', { class: 'booking-head' }, el('h2', {}, title), close), body)
  dialog.addEventListener('close', () => dialog.remove())
  document.body.append(dialog)
  dialog.showModal()
  return { dialog, body }
}

// The answer lost on the way back (no signal at the wrong moment): the request may still have worked, and a second try
// would be refused by then (the round closed, or the sign-in gone). So check this round is really gone from the rounds
// this email can open before saying it worked; anything else is a failure (Astra's review, round 2).
async function didItWork(businessId: number | null | undefined): Promise<boolean> {
  try {
    const r = await api<{ rounds: { businessId: number }[] }>('/api/me/rounds')
    return !!businessId && !r.rounds.some((x) => x.businessId === businessId)
  } catch (e) {
    const code = (e as { body?: { code?: string } }).body?.code
    return code === 'not_a_user' || code === 'business_suspended'
  }
}

const row = (drawing: Drawing, title: string, words: string): HTMLElement =>
  el('div', { class: 'acct-row' }, el('span', { class: 'acct-ring acct-ring-small', 'aria-hidden': 'true' }, lineDrawing(drawing, 18)), el('div', {}, el('b', {}, title), el('p', {}, words)))

function openCloseRound(roundName: string): void {
  const { dialog, body } = sheet('Delete your round')
  const input = el('input', { type: 'text', autocomplete: 'off', 'aria-label': `Type ${roundName} to be sure` }) as HTMLInputElement
  const err = el('p', { class: 'form-error', role: 'alert' })
  const go = el('button', { type: 'button', class: 'acct-red', disabled: '' }, 'Delete my round') as HTMLButtonElement
  const same = (): boolean => input.value.trim().toLowerCase().replace(/\s+/g, ' ') === roundName.trim().toLowerCase().replace(/\s+/g, ' ')
  input.addEventListener('input', () => { go.disabled = !same(); err.textContent = '' })
  body.append(
    row('arrowDown', '1. Download your lists first', 'Your customers, visits and payments as spreadsheets, from Your account. We also email you a copy before anything is deleted.'),
    row('bin', '2. What goes', 'Every customer, visit, payment, message and photo, and the sign-ins of everyone on the round. Once deleted, nobody can bring it back.'),
    row('send', '3. When', 'The round closes as soon as you tap Delete: nobody on it can open it. We delete it after sending your copy, and email you when it is done. Changed your mind before then? Reply to that email.'),
    el('label', { class: 'acct-type' }, el('span', {}, `Type ${roundName} to be sure`), input), err, go)
  go.addEventListener('click', async () => {
    if (!same()) return
    go.disabled = true; go.textContent = 'Closing…'; busy = true
    try {
      try { await postNow('/api/me/account', { action: 'close_round', name: input.value }) } catch (e) {
        if ((e as { status?: number }).status || !(await didItWork(getMe()?.businessId))) throw e
      }
      body.replaceChildren(
        el('p', {}, `${roundName} is closed.`),
        el('p', {}, "We'll email you a copy of your lists, then delete everything and email you when it is done. Changed your mind? Reply to that email."),
      )
      const out = el('button', { type: 'button', class: 'btn-secondary acct-out' }, 'Log out')
      out.addEventListener('click', () => void logOut(false, true))
      body.append(out)
      dialog.addEventListener('close', () => void logOut(false, true), { once: true })
      busy = false
      if (!dialog.open) void logOut(false, true)
    } catch (e) {
      busy = false
      go.disabled = false; go.textContent = 'Delete my round'
      const code = (e as { body?: { code?: string } }).body?.code
      err.textContent = code === 'name' ? "That isn't your round's name." : code === 'kept' ? 'This round is closed by Roundly itself, not from here. Email us.' : "Couldn't close it. Check your signal and try again."
    }
  })
  input.focus()
}

function openDeleteMe(roundName: string): void {
  const { body } = sheet('Delete your account')
  const err = el('p', { class: 'form-error', role: 'alert' })
  const go = el('button', { type: 'button', class: 'acct-red' }, 'Delete my account') as HTMLButtonElement
  body.append(
    row('door', 'You can no longer sign in', `Your sign-in on ${roundName} goes straight away.`),
    row('lock', 'The round keeps its own records', `The jobs you did stay in ${roundName}'s diary and money: they are the owner's business records. Ask the owner if you want your name taken off them.`),
    err, go)
  go.addEventListener('click', async () => {
    go.disabled = true; go.textContent = 'Deleting…'; busy = true
    try {
      try { await postNow('/api/me/account', { action: 'delete_me' }) } catch (e) {
        if ((e as { status?: number }).status || !(await didItWork(getMe()?.businessId))) throw e
      }
      await logOut(false, true)
    } catch {
      busy = false
      go.disabled = false; go.textContent = 'Delete my account'
      err.textContent = "Couldn't delete it. Check your signal and try again."
    }
  })
}
